'use client';

import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Icon } from '../../icons';
import { Button } from '../Button/Button';
import { FilterChip } from '../FilterChip/FilterChip';
import { FilterDropdown } from '../FilterDropdown/FilterDropdown';
import type {
  ActiveFilter,
  FilterDefinition,
  FilterOperator,
} from '@/types/tableViews';

import './FilterBar.css';

export interface FilterBarProps {
  filters: ActiveFilter[];
  definitions: FilterDefinition[];
  onAddFilter: (filterKey: string, operator: FilterOperator, value: string | string[]) => void;
  onUpdateFilter: (filterId: string, updates: Partial<Pick<ActiveFilter, 'operator' | 'value' | 'filterKey'>>) => void;
  onRemoveFilter: (filterId: string) => void;
  onClearFilters: () => void;
  onSaveFilter?: () => void;
  isDirty?: boolean;
  /** When false, the "Reset Filter" button is rendered disabled — there's
   * nothing to reset to (filters already match the active view's saved state). */
  isFiltersDirty?: boolean;
  isDefaultView?: boolean;
  showDropdown?: boolean;
  onDropdownChange?: (open: boolean) => void;
  /** When provided, the FilterDropdown anchors to this element instead of the internal "Add Filter" button */
  anchorOverrideRef?: React.RefObject<HTMLElement | null>;
  className?: string;
}

export function FilterBar({
  filters,
  definitions,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
  onClearFilters,
  onSaveFilter,
  isDirty = false,
  isFiltersDirty = true,
  isDefaultView = false,
  showDropdown,
  onDropdownChange,
  anchorOverrideRef,
  className = '',
}: FilterBarProps) {
  const [internalDropdownOpen, setInternalDropdownOpen] = useState(false);
  const addBtnRef = useRef<HTMLButtonElement | null>(null);

  const dropdownOpen = showDropdown ?? internalDropdownOpen;
  const setDropdownOpen = (open: boolean) => {
    setInternalDropdownOpen(open);
    onDropdownChange?.(open);
  };

  const handleAddClick = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const effectiveAnchorRef = anchorOverrideRef ?? addBtnRef;

  if (filters.length === 0 && !dropdownOpen) return null;

  if (filters.length === 0 && dropdownOpen && anchorOverrideRef) {
    return (
      <AnimatePresence>
        <FilterDropdown
          definitions={definitions}
          open={dropdownOpen}
          onClose={() => setDropdownOpen(false)}
          onAddFilter={onAddFilter}
          anchorRef={anchorOverrideRef}
        />
      </AnimatePresence>
    );
  }

  return (
    <div className={`filter-bar ${className}`.trim()}>
      <div className="filter-bar__chips">
        <AnimatePresence mode="popLayout">
          {filters.map((f) => {
            const def = definitions.find((d) => d.key === f.filterKey);
            if (!def) return null;
            return (
              <motion.div
                key={f.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.15 }}
              >
                <FilterChip
                  filter={f}
                  definition={def}
                  allDefinitions={definitions}
                  onUpdate={onUpdateFilter}
                  onRemove={onRemoveFilter}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>

        <button
          ref={addBtnRef}
          className="filter-bar__add-btn"
          onClick={handleAddClick}
          type="button"
        >
          <Icon name="Plus" size={13} />
          <span>Add Filter</span>
        </button>
      </div>

      <div className="filter-bar__actions">
        {filters.length > 0 && (
          <button
            className="filter-bar__reset-btn"
            onClick={onClearFilters}
            type="button"
            disabled={!isFiltersDirty}
            title={isFiltersDirty ? undefined : 'Filters already match the saved view'}
          >
            Reset Filter
          </button>
        )}
        {onSaveFilter && isDirty && (
          isDefaultView ? (
            <span title="Default views can't be modified. Save as a new view instead.">
              <Button
                variant="primary"
                size="sm"
                leadingVisual="Save"
                disabled
              >
                Save Filter
              </Button>
            </span>
          ) : (
            <Button
              variant="primary"
              size="sm"
              leadingVisual="Save"
              onClick={onSaveFilter}
            >
              Save Filter
            </Button>
          )
        )}
      </div>

      <AnimatePresence>
        {dropdownOpen && (
          <FilterDropdown
            definitions={definitions}
            open={dropdownOpen}
            onClose={() => setDropdownOpen(false)}
            onAddFilter={onAddFilter}
            anchorRef={effectiveAnchorRef}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
